import { HeatMap, type HeatMapCell } from "../primitives/HeatMap";
import { cn } from "../../lib/cn";

type MetricView = "words" | "citations" | "completeness";

interface SectionHeatMapProps {
  sectionWordCounts: Record<string, number>;
  citationsPerSection: Record<string, number>;
  sectionCompleteness: Record<string, number>;
  view?: MetricView;
  onSectionClick?: (sectionTitle: string) => void;
  className?: string;
}

function SectionHeatMap({
  sectionWordCounts,
  citationsPerSection,
  sectionCompleteness,
  view = "words",
  onSectionClick,
  className,
}: SectionHeatMapProps) {
  const source = view === "words"
    ? sectionWordCounts
    : view === "citations"
      ? citationsPerSection
      : sectionCompleteness;

  const cells: HeatMapCell[] = Object.entries(source).map(([title, value]) => ({
    id: title,
    label: title.length > 8 ? title.slice(0, 7) + "…" : title,
    value: view === "completeness" ? Math.round(value * 100) : value,
  }));

  if (cells.length === 0) {
    return (
      <p className="text-[11px] text-[var(--text-muted)] italic">
        No sections detected.
      </p>
    );
  }

  const formatFn = (cell: HeatMapCell) => {
    if (view === "completeness") return `${cell.id}: ${cell.value}%`;
    if (view === "citations") return `${cell.id}: ${cell.value} citations`;
    return `${cell.id}: ${cell.value} words`;
  };

  return (
    <div className={cn("", className)}>
      <HeatMap
        cells={cells}
        columns={Math.min(4, cells.length)}
        cellSize={52}
        gap={3}
        onCellClick={onSectionClick ? (c) => onSectionClick(c.id) : undefined}
        format={formatFn}
      />
    </div>
  );
}

SectionHeatMap.displayName = "SectionHeatMap";

export { SectionHeatMap };
export type { SectionHeatMapProps, MetricView };
